import React, { useEffect } from 'react';
import SimpleSlider from '../../components/common/SimpleSlider.js'
import { BsQuestionCircle } from '@react-icons/all-files/bs/BsQuestionCircle'
import { GiCheckeredFlag } from "@react-icons/all-files/gi/GiCheckeredFlag";
import { ImFilter } from '@react-icons/all-files/im/ImFilter'
import Link from 'next/link'
import PAGEURL from '../../constants/PAGEURL'
import Head from 'next/head'
import Image from 'next/image'
import Circles from '../../components/common/Circles'

const OG_IMAGE = 'https://simplybuy.io/simplybuy-favicon.png'

const CIRCLE_IDS = [
  'circle1', 'circle2', 'circle3', 'circle4', 'circle5', 'circle6', 'circle7',
  'circle8', 'circle9', 'circle10', 'circle11', 'circle12', 'circle13',
  'circle14', 'circle15', 'circle16', 'circle17', 'circle18', 'circle19',
]

function setCirclesCombined(combined) {
  CIRCLE_IDS.forEach((id, i) => {
    const el = document.getElementById(id)
    if (!el) return
    el.classList.toggle(`circle${i + 1}Combine`, combined)
  })
  const tagline = document.getElementById('circles-tagline')
  if (tagline) {
    tagline.classList.toggle('show-tagline', combined)
  }
}

export default function Home() {
  useEffect(() => {
    const target = document.getElementById('home-circles')
    if (!target || typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Combine when the logo block is meaningfully on screen; reverse when it leaves.
          setCirclesCombined(entry.isIntersecting && entry.intersectionRatio >= 0.35)
        })
      },
      {
        threshold: [0, 0.35, 0.6],
        // Hold combine until the block has scrolled ~600px further into view
        rootMargin: '0px 0px -600px 0px',
      }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, []);

  const title = 'SimplyBuy | Ski & Snowboard Pickers'
  const description =
    'Free ski and snowboard pickers trusted by Reddit riders. Answer a few questions and get a personalized shortlist in minutes—clear picks, not endless review tabs.'

  return (
    <div className="home">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://simplybuy.io/" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Head>
      <section className="home-hero">
        <div className="home-hero-glow" aria-hidden="true" />
        <div className="home-hero-inner">
          <div className="img-container home-hero-logo">
            <Image
              src={`/simplybuy-logo.webp`}
              width={300}
              height={60}
              priority={true}
              alt="SimplyBuy"
              placeholder="blur"
              blurDataURL={`/simplybuy-logo.webp`}
            />
          </div>
          <h1 className="home-hero-title">
            <span className="home-hero-title-line">Ski &amp; Snowboard Pickers</span>
            <span className="home-hero-title-line home-hero-title-accent">A curated shortlist, built for you</span>
          </h1>

          <div className="home-primary-ctas">
            <Link href={PAGEURL.PRODUCT_PICKER('ski', '0')} className="home-primary-cta home-primary-cta--ski">
              Find My Skis
            </Link>
            <Link href={PAGEURL.PRODUCT_PICKER('snowboard', '0')} className="home-primary-cta home-primary-cta--board">
              Find My Snowboard
            </Link>
          </div>

          <div className="home-hero-quotes">
            <blockquote className="home-hero-quote home-hero-quote--ski">
              <p>
                “Nice tool! Been a sales rep in the ski business for 37 years, and this is the best tool I have seen.”
              </p>
              <footer>
                <cite>
                  <a
                    href="https://www.reddit.com/r/skiing/comments/smt0of/still_looking_for_new_skis_this_season_i_made_a/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Reddit · r/skiing
                  </a>
                </cite>
              </footer>
            </blockquote>
            <blockquote className="home-hero-quote home-hero-quote--board">
              <p>
                “This tool is going to get you where you want to go. Used it to pick both my boards this year, and both have been fantastic.”
              </p>
              <footer>
                <cite>
                  <a
                    href="https://www.reddit.com/r/snowboarding/comments/q81eo9/for_anyone_looking_for_a_new_snowboard_for_the/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Reddit · r/snowboarding
                  </a>
                </cite>
              </footer>
            </blockquote>
          </div>

          <a
            href="#more-pickers"
            className="home-hero-more"
            onClick={(e) => {
              e.preventDefault()
              const el = document.getElementById('more-pickers')
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }}
          >
            <span>More pickers</span>
            <span className="home-hero-more-arrow" aria-hidden="true">↓</span>
          </a>
        </div>
      </section>

      <div className="home-container">
        <div id="more-pickers" className="psg-container home-page orange home-more-pickers">
          <div className="select-product home-more-label">More product pickers</div>
          <div>
            <div className="product-category mt-md pch">Outdoor/Sports Gear</div>
            <div className="products-flex home-outdoor-pickers">
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('mountain-bike', '0')}>
                  <div className="product-option">Mountain Bikes</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('pickleball-paddle', '0')}>
                  <div className="product-option">Pickleball Paddles</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('snowboard-binding', '0')}>
                  <div className="product-option">Snowboard Bindings</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('snowboard-boots', '0')}>
                  <div className="product-option">Snowboard Boots</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('snowboard-jacket', '0')}>
                  <div className="product-option">Snowboard Jackets</div>
                </Link>
              </div>
            </div>
          </div>
          <div>
            <div className="product-category pch2">Tech</div>
            <div className="products-flex max-size">
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('headphones', '0')}>
                  <div className="product-option">Headphones</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.PRODUCT_PICKER('monitor', '0')}>
                  <div className="product-option">Monitors</div>
                </Link>
              </div>
              <div className="product-container">
                <Link href={PAGEURL.BUYANGLE('tv')}>
                  <div className="product-option">TVs</div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div id="home-circles">
          <Circles/>
        </div>

        <div className="pp-symbols home-how-it-works">
          <div className="process">How it works</div>
          <div className="symbols-container">
            <div className="symbol">
              <div className="stat-block">
                <div className="stat-label">
                  <div className="icon-wrapper">
                    <BsQuestionCircle />
                  </div>
                  <div className="stat-name">Learn</div>
                </div>
                <div className="">
                  Answer plain-language questions. We explain the tech as you go.
                </div>
              </div>
            </div>
            <div className="symbol">
              <div className="stat-block">
                <div className="stat-label">
                  <div className="icon-wrapper">
                    <ImFilter />
                  </div>
                  <div className="stat-name">Filter</div>
                </div>
                <div className="">
                  We narrow the field to options that fit how you use the product.
                </div>
              </div>
            </div>
            <div className="symbol">
              <div className="stat-block">
                <div className="stat-label">
                  <div className="icon-wrapper">
                    <GiCheckeredFlag />
                  </div>
                  <div className="stat-name">Buy</div>
                </div>
                <div className="">
                  Leave with a shortlist, not another weekend of tabs.
                </div>
              </div>
            </div>
          </div>
        </div>

        <SimpleSlider />
        <div className="home-guides-link">
          <Link href="/blog">Read ski &amp; snowboard guides</Link>
          {' · '}
          <Link href="/FAQs">FAQs</Link>
        </div>
      </div>
    </div>
  )
}
